import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Badge,
  Divider,
  Tooltip,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Label as LabelIcon,
  Add as AddIcon,
  AllInclusive as AllIcon,
  HelpOutline as UncategorizedIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getButtonStyles, BUTTON_COLORS } from '../../config/buttonStyles';
import { useTheme } from '../../utils/ThemeContext';
import { getThemeAwareColor, getCategoryStyles } from '../../utils/categoryColors';
import { useNavigate } from 'react-router-dom';

function CategorySidebar({ 
  categories, 
  selectedCategory,  // Now an array for multi-select
  onSelectCategory, 
  totalArticles,
  uncategorizedCount,
  onCreateCategory,
  onEditCategory,
  onDeleteCategory,
  onClose
}) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Check if a category is selected
  const isCategorySelected = (categoryId) => {
    return Array.isArray(selectedCategory) && selectedCategory.includes(categoryId);
  };
  
  // Handle delete with confirmation
  const handleDelete = (e, category) => {
    e.stopPropagation(); // Prevent category selection
    setCategoryToDelete(category);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (categoryToDelete) {
      onDeleteCategory(categoryToDelete.id);
    }
    setDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };
  
  // Handle edit
  const handleEdit = (e, category) => {
    e.stopPropagation(); // Prevent category selection
    onEditCategory(category);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--surface-1)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: '1px solid var(--border)',
          background: 'linear-gradient(135deg, rgba(108, 48, 130, 0.1), rgba(70, 130, 48, 0.1))',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <LabelIcon sx={{ color: 'var(--neon-cyan)' }} />
            Categories
          </Typography>
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ 
                color: 'var(--text-muted)',
                '&:hover': { color: 'var(--neon-cyan)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" sx={{ color: 'var(--text-muted)', mt: 0.5, display: 'block' }}>
          Filter articles by category
        </Typography>
      </Box>

      {/* Category List */}
      <List sx={{ flex: 1, p: 1.5 }}>
        {/* All Articles */}
        <ListItemButton
          selected={selectedCategory.length === 0}
          onClick={() => onSelectCategory(null)}
          disableRipple
          sx={{
            borderRadius: '8px',
            mb: 1,
            padding: '8px !important',
            minHeight: 'unset',
            transition: 'background-color 0.2s, box-shadow 0.2s',
            '&:hover': {
              backgroundColor: 'var(--surface-2)',
              padding: '8px !important'
            },
            '&.Mui-selected': {
              background: 'var(--accent-gradient)',
              color: '#fff !important',
              boxShadow: '0 4px 12px rgba(108, 48, 130, 0.3)',
              padding: '8px !important',
              '& .MuiListItemIcon-root': {
                color: '#fff !important'
              },
              '& .MuiListItemText-primary': {
                color: '#fff !important',
                fontWeight: 600
              },
              '& .MuiListItemText-secondary': {
                color: 'rgba(255,255,255,0.8) !important'
              },
              '& .MuiTypography-root': {
                color: '#fff !important'
              },
              '&:hover': {
                background: 'var(--accent-gradient)',
                padding: '8px !important'
              }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AllIcon sx={{ color: selectedCategory.length === 0 ? '#fff' : 'var(--neon-cyan)' }} />
          </ListItemIcon>
          <ListItemText 
            primary={
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: selectedCategory.length === 0 ? 600 : 500,
                  color: selectedCategory.length === 0 ? '#fff !important' : 'var(--text-primary)'
                }}
              >
                All Articles
              </Typography>
            }
            secondary={
              <Typography 
                variant="caption" 
                sx={{ 
                  color: selectedCategory.length === 0 ? 'rgba(255,255,255,0.8) !important' : 'var(--text-muted)'
                }}
              >
                {totalArticles} articles
              </Typography>
            }
          />
        </ListItemButton>

        <Divider sx={{ my: 1 }} />

        {/* User Categories - Multi-select with Checkboxes */}
        {categories.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <LabelIcon sx={{ fontSize: 48, color: 'var(--text-muted)', opacity: 0.3, mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
              No categories yet
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
              Create your first category below
            </Typography>
          </Box>
        )}
        {categories.map((category) => {
          const isSelected = isCategorySelected(category.id);
          const themeColor = getThemeAwareColor(category.color, isDarkMode, 0);
          const styles = getCategoryStyles(category.color, isDarkMode, isSelected);
          
          return (
            <Tooltip key={category.id} title={category.name} placement="right">
              <ListItemButton
                selected={isSelected}
                onClick={() => onSelectCategory(category.id)}
                disableRipple
                sx={{
                  borderRadius: '8px',
                  mb: 0.5,
                  transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '2px solid transparent',
                  borderColor: isSelected ? themeColor : 'transparent',
                  padding: '8px !important',
                  minHeight: 'unset',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: themeColor,
                    opacity: 1,
                    transition: 'background-color 0.2s'
                  },
                  '&:hover': {
                    backgroundColor: 'var(--surface-2)',
                    borderColor: themeColor,
                    padding: '8px !important'
                  },
                  '&.Mui-selected': {
                    backgroundColor: styles.backgroundColor,
                    borderColor: themeColor,
                    boxShadow: isDarkMode ? `0 4px 12px ${themeColor}40` : `0 2px 8px ${themeColor}30`,
                    padding: '8px !important',
                    '&:hover': {
                      backgroundColor: styles.backgroundColor,
                      padding: '8px !important'
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    sx={{
                      color: `${themeColor} !important`,
                      '&.Mui-checked': {
                        color: `${themeColor} !important`
                      },
                      '&:hover': {
                        backgroundColor: styles.backgroundColor
                      }
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ 
                    margin: 0,
                    '& .MuiListItemText-primary': {
                      margin: 0
                    }
                  }}
                  primary={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 1
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isSelected ? `${themeColor} !important` : 'var(--text-primary)',
                          fontWeight: isSelected ? 700 : 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0
                        }}
                      >
                        {category.name}
                      </Typography>
                      <Badge 
                        badgeContent={category.article_count || 0}
                        sx={{
                          flexShrink: 0,
                          '& .MuiBadge-badge': {
                            backgroundColor: isSelected ? styles.backgroundColor : 'transparent',
                            color: `${themeColor} !important`,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px',
                            minWidth: '24px',
                            borderRadius: '12px',
                            border: `2px solid ${themeColor}`,
                            padding: '0 6px'
                          }
                        }}
                      />
                    </Box>
                  }
                />
                {/* Action Buttons - Show on hover */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.25,
                    ml: 1,
                    opacity: 0,
                    transition: 'all 0.2s',
                    '.MuiListItemButton-root:hover &': {
                      opacity: 1
                    }
                  }}
                >
                  <Tooltip title="Edit category" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => handleEdit(e, category)}
                      sx={{
                        padding: '4px',
                        color: themeColor,
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: styles.backgroundColor,
                          color: themeColor
                        }
                      }}
                    >
                      <EditIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete category" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(e, category)}
                      sx={{
                        padding: '4px',
                        color: 'var(--error)',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          color: '#d32f2f'
                        }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItemButton>
            </Tooltip>
          );
        })}

        <Divider sx={{ my: 1, borderColor: 'var(--border)' }} />

        {/* Uncategorized - Multi-select with Checkbox */}
        <ListItemButton
          selected={isCategorySelected('uncategorized')}
          onClick={() => onSelectCategory('uncategorized')}
          disableRipple
          sx={{
            borderRadius: '8px',
            mb: 0.5,
            transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
            position: 'relative',
            overflow: 'hidden',
            border: '2px solid transparent',
            borderColor: isCategorySelected('uncategorized') ? 'var(--text-muted)' : 'transparent',
            padding: '8px !important',
            minHeight: 'unset',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              backgroundColor: 'var(--text-muted)',
              opacity: 1,
              transition: 'background-color 0.2s'
            },
            '&:hover': {
              backgroundColor: 'var(--surface-2)',
              borderColor: 'var(--text-muted)',
              padding: '8px !important'
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(158, 158, 158, 0.1)',
              borderColor: 'var(--text-muted)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '8px !important',
              '& .MuiListItemText-primary': {
                color: 'var(--text-primary)',
                fontWeight: 700
              },
              '&:hover': {
                padding: '8px !important'
              }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Checkbox
              checked={isCategorySelected('uncategorized')}
              size="small"
              sx={{
                color: 'var(--text-muted) !important',
                '&.Mui-checked': {
                  color: 'var(--text-muted) !important'
                },
                '&:hover': {
                  backgroundColor: 'rgba(158, 158, 158, 0.1)'
                }
              }}
            />
          </ListItemIcon>
          <ListItemText
            sx={{ 
              margin: 0,
              '& .MuiListItemText-primary': {
                margin: 0
              }
            }}
            primary={
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                gap: 1
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isCategorySelected('uncategorized') ? 700 : 500,
                    color: isCategorySelected('uncategorized') ? 'var(--text-muted)' : 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  Uncategorized
                </Typography>
                <Badge 
                  badgeContent={uncategorizedCount}
                  sx={{
                    flexShrink: 0,
                    '& .MuiBadge-badge': {
                      backgroundColor: isCategorySelected('uncategorized') ? 'rgba(158, 158, 158, 0.15)' : 'transparent',
                      color: 'var(--text-muted) !important',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: '24px',
                      minWidth: '24px',
                      borderRadius: '12px',
                      border: '2px solid var(--text-muted)',
                      padding: '0 6px'
                    }
                  }}
                />
              </Box>
            }
          />
          {/* Spacer to match action buttons width on user categories */}
          <Box sx={{ width: '56px', flexShrink: 0 }} />
        </ListItemButton>
      </List>

      {/* Footer - Create Category Button */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: '1px solid var(--border)',
          background: 'linear-gradient(180deg, transparent, rgba(108, 48, 130, 0.02))'
        }}
      >
        <Button
          data-tutorial="create-category-button"
          fullWidth
          size="medium"
          startIcon={<AddIcon />}
          onClick={onCreateCategory}
          className="btn-modern"
          sx={getButtonStyles(false, isDarkMode, 'generic')}
        >
          New Category
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'var(--text-primary)', 
          borderBottom: '1px solid var(--glass-border)',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon style={{ fontSize: '1.2rem', color: '#ff4444' }} />
            Delete Category
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography sx={{ color: 'var(--text-secondary)' }}>
            Are you sure you want to delete "{categoryToDelete?.name}"? This will remove the category from all articles and they will become uncategorized.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={cancelDelete}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'generic')}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            className="btn-modern"
            sx={getButtonStyles(false, isDarkMode, 'delete')}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategorySidebar;

